/**
 * How a study is named in the interface.
 *
 * Client, 2026-10-14: "Study Name to be displayed everywhere instead of Study
 * ID - check complete application. filter and dropdown everywhere show the
 * study name not study ID".
 *
 * WHAT WAS WRONG. Three different things were being shown as a study's name:
 *
 *   {s.protocol_number} — {s.title}            (most dropdowns: ID first)
 *   {s.protocol_number}                         (Training filter: ID only)
 *   {study_id.slice(0, 8)}                      (fallbacks: a raw UUID stub)
 *
 * All three lead with an identifier. The last is the worst — `b3f1a9c2` is not
 * a name, and it appeared whenever the study list had not loaded or the study
 * was not in scope.
 *
 * THE RULE. The name leads; the protocol number follows as secondary context.
 * This is the shape the training log already adopted on 2026-10-09
 * ("Diabetes Phase II · STD-2026-0020") and it is kept rather than dropping
 * the protocol number entirely, because the protocol number is what a sponsor
 * or a monitor quotes and losing it has a real cost. Where there is only room
 * for one string, use studyName().
 *
 * A RAW UUID IS NEVER THE PRIMARY LABEL. With no title we fall back to the
 * protocol number, and only with neither to a short id — prefixed so it reads
 * as a reference rather than as a name.
 */

export interface StudyLike {
  study_id: string;
  title?: string | null;
  protocol_number?: string | null;
}

/** A short, prefixed id. Last resort only — never a bare UUID fragment. */
function shortRef(studyId: string): string {
  return studyId ? `Study ${studyId.slice(0, 8)}` : 'Unknown study';
}

/** The study's name on its own. Use where one string is all there is room for. */
export function studyName(study: StudyLike | null | undefined): string {
  if (!study) return 'Unknown study';
  const title = study.title?.trim();
  if (title) return title;
  const protocol = study.protocol_number?.trim();
  if (protocol) return protocol;
  return shortRef(study.study_id);
}

/**
 * Name first, protocol number second: "Diabetes Phase II · STD-2026-0020".
 *
 * The protocol number is omitted when absent or when it is already standing
 * in as the name, so nothing renders as "STD-2026-0020 · STD-2026-0020".
 */
export function studyLabel(study: StudyLike | null | undefined): string {
  if (!study) return 'Unknown study';
  const name = studyName(study);
  const protocol = study.protocol_number?.trim();
  return protocol && protocol !== name ? `${name} · ${protocol}` : name;
}

/**
 * The same, for the many places that hold a study_id and a lookup map rather
 * than the study itself. A miss is labelled, not rendered as a UUID.
 */
export function studyLabelById(
  studies: ReadonlyMap<string, StudyLike> | undefined,
  studyId: string | null | undefined,
): string {
  if (!studyId) return '—';
  const study = studies?.get(studyId);
  return study ? studyLabel(study) : shortRef(studyId);
}

/** Name-only variant of studyLabelById, for tight table cells. */
export function studyNameById(
  studies: ReadonlyMap<string, StudyLike> | undefined,
  studyId: string | null | undefined,
): string {
  if (!studyId) return '—';
  const study = studies?.get(studyId);
  return study ? studyName(study) : shortRef(studyId);
}
